import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import TeamSwitcher from './TeamSwitcher';
import TeamOverview from './team-management/TeamOverview';
import RosterPanel from './team-management/RosterPanel';
import SquadsPanel from './team-management/SquadsPanel';
import TeamInvitesPanel from './team-management/TeamInvitesPanel';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'roster', label: 'Roster' },
  { id: 'squads', label: 'Squads' },
  { id: 'invites', label: 'Invites' },
];

/**
 * Coach-scoped admin dashboard for the caller's active team: roster, squads,
 * and invites. Multi-team coaches switch teams via the same TeamSwitcher used
 * in the header; switching reloads every panel for the newly active team.
 */
export default function CoachTeamManagement() {
  const { user, token, impersonatedClubCode } = useAuth();
  const [section, setSection] = useState('overview');
  const [refreshKey, setRefreshKey] = useState(0);
  const [clubTeams, setClubTeams] = useState(null);

  // Deep link support: ?tab=teamManagement&section=squads (from Workout/Plan Builder "Manage squads" links)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestedSection = params.get('section');
    if (requestedSection && SECTIONS.some((s) => s.id === requestedSection)) {
      setSection(requestedSection);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadClubTeams = async () => {
      try {
        const res = await fetch(`${API_URL}/api/teams/settings`, {
          headers: getAuthHeaders(token)
        });
        if (!res.ok) {
          if (!cancelled) setClubTeams([]);
          return;
        }
        const data = await res.json();
        if (!cancelled) setClubTeams(data.availableTeams || []);
      } catch (err) {
        console.error('Error loading club teams:', err);
        if (!cancelled) setClubTeams([]);
      }
    };
    loadClubTeams();
    return () => { cancelled = true; };
  }, [token, impersonatedClubCode, user?.team]);

  const jwtTeam = user?.team;
  const activeTeam = clubTeams == null
    ? jwtTeam
    : (clubTeams.includes(jwtTeam) ? jwtTeam : (clubTeams[0] || null));
  const switcherTeams = (clubTeams && clubTeams.length > 0)
    ? clubTeams
    : (Array.isArray(user?.teams) && user.teams.length > 0 ? user.teams : (jwtTeam ? [jwtTeam] : []));

  if (clubTeams == null) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Team Management</h2>
        <p className="text-gray-600">Loading team...</p>
      </div>
    );
  }

  if (!activeTeam) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Team Management</h2>
        <p className="text-gray-600">You're not assigned to a team yet.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-2">
        <h2 className="text-2xl font-bold">Team Management</h2>
        {switcherTeams.length > 1 && (
          <TeamSwitcher
            variant="page"
            teams={switcherTeams}
            value={activeTeam}
            onTeamSwitch={() => setRefreshKey((k) => k + 1)}
          />
        )}
      </div>
      <p className="text-gray-600 mb-6">
        Manage {activeTeam}'s roster, squads, and invitations. Club-wide settings live in Club Management.
      </p>

      <div className="flex gap-1 mb-6 border-b">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              section === s.id
                ? 'border-theme-primary text-theme-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === 'overview' && <TeamOverview key={`overview-${refreshKey}`} team={activeTeam} onNavigate={setSection} />}
      {section === 'roster' && <RosterPanel key={`roster-${refreshKey}`} team={activeTeam} />}
      {section === 'squads' && <SquadsPanel key={`squads-${refreshKey}`} team={activeTeam} />}
      {section === 'invites' && <TeamInvitesPanel key={`invites-${refreshKey}`} team={activeTeam} />}
    </div>
  );
}
